import { useState, useRef, useEffect } from "react";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { ProductJsonLd } from "@/components/JsonLd";
import SEOHead from "@/components/SEOHead";
import { staticMedia as media, buildAltText, type MediaItem } from "@/data/galleryMedia";
import { VISIBLE_PRODUCTS } from "@/data/products";

const Produkty = () => {
  const { t } = useTranslation();
  const variants = t("productsPage.variants", { returnObjects: true }) as { name: string; desc: string }[];
  const process = t("productsPage.process", { returnObjects: true }) as { title: string; desc: string }[];
  const applications = t("productsPage.applications", { returnObjects: true }) as string[];

  const [selected, setSelected] = useState<number | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [allMedia, setAllMedia] = useState<MediaItem[]>(media);

  useEffect(() => {
    const fetchCmsMedia = async () => {
      const { data } = await supabase
        .from("gallery_media")
        .select("*")
        .order("position", { ascending: true });
      if (data && data.length > 0) {
        const cmsItems: MediaItem[] = data.map((item, idx) => ({
          type: item.type as "image" | "video",
          src: item.url,
          alt: buildAltText(item, idx),
          poster: item.poster_url || undefined,
          mirror: item.mirror,
        }));
        setAllMedia([...cmsItems, ...media]);
      }
    };
    fetchCmsMedia();
  }, []);

  useEffect(() => {
    if (selected !== null && allMedia[selected].type === "video" && videoRef.current) {
      videoRef.current.play();
    }
  }, [selected]);

  useEffect(() => {
    if (!gridRef.current) return;
    const videos = gridRef.current.querySelectorAll("video[data-src]");
    if (!videos.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target as HTMLVideoElement;
          if (entry.isIntersecting) {
            if (!video.src && video.dataset.src) {
              video.src = video.dataset.src;
              video.load();
            }
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { rootMargin: "200px", threshold: 0.1 }
    );
    videos.forEach((v) => observer.observe(v));
    return () => observer.disconnect();
  }, [allMedia]);

  const selectedItem = selected !== null ? allMedia[selected] : null;

  return (
    <Layout>
      <SEOHead
        title="Produkty – naklejki 3D wypukłe zalewane żywicą"
        description="Oferta naklejek 3D zalewanych żywicą poliuretanową: folie białe, chromowe złote i srebrne, holograficzne, odblaskowe i świecące. Dowolny kształt — wyceń online."
      />
      <ProductJsonLd />
      <section className="gradient-navy py-20 lg:py-28">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-primary-foreground mb-4">
            {t("productsPage.title")}<span className="text-gradient-gold">{t("productsPage.titleGold")}</span>{t("productsPage.titleEnd")}
          </h1>
          <p className="text-primary-foreground/80 text-lg max-w-3xl mx-auto">{t("productsPage.subtitle")}</p>
        </div>
      </section>

      {/* Podstrony produktowe – linkowanie wewnętrzne */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6 text-center">
            Nasze produkty
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {VISIBLE_PRODUCTS.map((p) => (
              <li key={p.slug}>
                <Link
                  to={`/${p.slug}`}
                  className="block h-full bg-card border border-border rounded-xl p-5 hover:border-accent transition-colors"
                >
                  <span className="block font-semibold text-foreground mb-1">{p.name}</span>
                  <span className="block text-sm text-muted-foreground">{p.metaDescription}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>



      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-foreground mb-10 text-center">{t("productsPage.variantsTitle")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {variants.map((v, i) => (
              <div key={i} className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow hover:border-accent/40">
                <h3 className="font-semibold text-foreground mb-1">{v.name}</h3>
                <p className="text-muted-foreground text-sm">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mini galeria */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-foreground mb-6 text-center">{t("gallery.title")}</h2>
          <div ref={gridRef} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {allMedia.map((item, i) => (
              <button key={i} onClick={() => setSelected(i)} className="relative overflow-hidden rounded-lg aspect-square group cursor-pointer">
                {item.type === "image" ? (
                  <img src={item.src} alt={item.alt} width={600} height={600} decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                ) : (
                  <video
                    data-src={item.src}
                    poster={item.poster}
                    muted
                    playsInline
                    loop
                    preload="none"
                    className={`w-full h-full object-cover ${item.mirror ? '-scale-x-100' : ''}`}
                  />
                )}
                <div className="absolute inset-0 bg-navy-dark/0 group-hover:bg-navy-dark/40 transition-colors flex items-end p-2">
                  <span className="text-primary-foreground text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">{item.alt}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-secondary">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-foreground mb-10 text-center">{t("productsPage.processTitle")}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {process.map((p, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 rounded-full gradient-gold flex items-center justify-center text-accent-foreground font-bold text-xl mx-auto mb-3">{i + 1}</div>
                <h3 className="font-semibold text-foreground text-sm mb-1">{p.title}</h3>
                <p className="text-muted-foreground text-xs">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-foreground mb-8 text-center">{t("productsPage.applicationsTitle")}</h2>
          <div className="space-y-3">
            {applications.map((a, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-card border border-border rounded-xl">
                <span className="w-2 h-2 rounded-full bg-accent mt-2 shrink-0" />
                <p className="text-foreground">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {selectedItem && (
        <div className="fixed inset-0 z-50 bg-navy-dark/90 flex items-center justify-center p-4 animate-fade-in" onClick={() => setSelected(null)}>
          <button onClick={() => setSelected(null)} className="absolute top-4 right-4 text-primary-foreground/80 hover:text-primary-foreground z-10">
            <X className="w-8 h-8" />
          </button>
          {selectedItem.type === "image" ? (
            <img src={selectedItem.src} alt={selectedItem.alt} width={1200} height={1200} decoding="async" className="max-w-full max-h-[85vh] object-contain rounded-xl" />
          ) : (
            <video
              ref={videoRef}
              src={selectedItem.src}
              controls
              autoPlay
              muted
              loop
              className={`max-w-full max-h-[85vh] rounded-xl ${selectedItem.type === 'video' && selectedItem.mirror ? '-scale-x-100' : ''}`}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      )}
    </Layout>
  );
};

export default Produkty;
